/**
 * Documentation Store
 * 
 * Zustand store for managing documentation state.
 * Supports three states: closed, open (unpinned), open (pinned)
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DocPage, DocSearchResult, DocNavNode } from '../docs/types';
import { docRegistry, searchDocs, getNavTree } from '../docs';

// Constants
const STORAGE_KEY = 'quanthop-docs';
const DEFAULT_WIDTH = 800;
const MIN_WIDTH = 640;
const MAX_WIDTH = 1100;

interface DocsStore {
  // State
  isOpen: boolean;
  isPinned: boolean;
  width: number;
  activeDocId: string | null;
  activePage: DocPage | null;
  history: string[];
  historyIndex: number;
  searchQuery: string;
  searchResults: DocSearchResult[];
  expandedCategories: Record<string, boolean>;
  navTree: DocNavNode[];

  // Actions
  openDocs: (docId?: string) => void;
  closeDocs: () => void;
  toggleDocs: () => void;
  togglePin: () => void;
  setWidth: (width: number) => void;
  navigateTo: (docId: string) => void;
  goBack: () => void;
  goForward: () => void;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  toggleCategory: (categoryId: string) => void;
  expandCategory: (categoryId: string) => void;
  
  // Helpers
  closeIfUnpinned: () => void;
}

export const useDocsStore = create<DocsStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: false,
      isPinned: false,
      width: DEFAULT_WIDTH,
      activeDocId: null,
      activePage: null,
      history: [],
      historyIndex: -1,
      searchQuery: '',
      searchResults: [],
      expandedCategories: { 'getting-started': true },
      navTree: getNavTree(),

      // Actions
      openDocs: (docId) => {
        set((state) => {
          // Default to overview if no docId provided and no active page
          const targetDocId = docId || (state.activePage ? null : 'getting-started/overview');
          
          if (targetDocId) {
            const page = docRegistry.get(targetDocId);
            if (page) {
              const newHistory = state.history.slice(0, state.historyIndex + 1);
              newHistory.push(targetDocId);
              return {
                isOpen: true,
                activeDocId: targetDocId,
                activePage: page,
                history: newHistory,
                historyIndex: newHistory.length - 1,
                expandedCategories: { ...state.expandedCategories, [page.category]: true },
              };
            }
          }
          return { isOpen: true };
        });
      },

      closeDocs: () => {
        set({ isOpen: false });
      },

      toggleDocs: () => {
        set((state) => {
          const willOpen = !state.isOpen;
          
          // When opening and no active page, default to overview
          if (willOpen && !state.activePage) {
            const page = docRegistry.get('getting-started/overview');
            if (page) {
              return {
                isOpen: true,
                activeDocId: 'getting-started/overview',
                activePage: page,
                history: ['getting-started/overview'],
                historyIndex: 0,
                expandedCategories: { ...state.expandedCategories, 'getting-started': true },
              };
            }
          }
          
          return { isOpen: willOpen };
        });
      },

      togglePin: () => {
        set((state) => ({ isPinned: !state.isPinned }));
      },

      setWidth: (width) => {
        // Clamp width to min/max
        const clampedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
        set({ width: clampedWidth });
      },

      // Close only if not pinned (for navigation events, etc.)
      closeIfUnpinned: () => {
        const { isPinned } = get();
        if (!isPinned) {
          set({ isOpen: false });
        }
      },

      navigateTo: (docId) => {
        const page = docRegistry.get(docId);
        if (!page) {
          return;
        }

        set((state) => {
          const newHistory = state.history.slice(0, state.historyIndex + 1);
          if (newHistory[newHistory.length - 1] !== docId) {
            newHistory.push(docId);
          }
          
          return {
            activeDocId: docId,
            activePage: page,
            searchQuery: '',
            searchResults: [],
            history: newHistory,
            historyIndex: newHistory.length - 1,
            expandedCategories: { ...state.expandedCategories, [page.category]: true },
          };
        });
      },

      goBack: () => {
        const { history, historyIndex } = get();
        if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          const docId = history[newIndex];
          const page = docRegistry.get(docId);
          
          set({
            historyIndex: newIndex,
            activeDocId: docId,
            activePage: page || null,
          });
        }
      },

      goForward: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const newIndex = historyIndex + 1;
          const docId = history[newIndex];
          const page = docRegistry.get(docId);
          
          set({
            historyIndex: newIndex,
            activeDocId: docId,
            activePage: page || null,
          });
        }
      },

      setSearchQuery: (query) => {
        set({
          searchQuery: query,
          searchResults: query.length >= 2 ? searchDocs(query) : [],
        });
      },

      clearSearch: () => {
        set({
          searchQuery: '',
          searchResults: [],
        });
      },

      toggleCategory: (categoryId) => {
        set((state) => ({
          expandedCategories: {
            ...state.expandedCategories,
            [categoryId]: !state.expandedCategories[categoryId],
          },
        }));
      },

      expandCategory: (categoryId) => {
        set((state) => ({
          expandedCategories: {
            ...state.expandedCategories,
            [categoryId]: true,
          },
        }));
      },
    }),
    {
      name: STORAGE_KEY,
      // Only persist width and pinned state, NOT isOpen
      partialize: (state) => ({
        width: state.width,
        isPinned: state.isPinned,
      }),
    }
  )
);

// Selector hooks for common patterns
export const useDocsOpen = () => useDocsStore((s) => s.isOpen);
export const useDocsPinned = () => useDocsStore((s) => s.isPinned);
export const useActivePage = () => useDocsStore((s) => s.activePage);
export const useDocsSearch = () => useDocsStore((s) => ({ 
  query: s.searchQuery, 
  results: s.searchResults,
  setQuery: s.setSearchQuery,
  clear: s.clearSearch 
}));

// Constants export for components
export { MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH };
